export type DamageType = 'slashing' | 'piercing' | 'bludgeoning' | 'fire' | 'cold' | 'lightning' | 'psychic' | 'radiant' | 'necrotic';
export interface Attributes { strength: number; dexterity: number; intelligence: number; will: number }
/** The stat an attack adds for this wielder. */
export function attackStat(attack: Pick<AttackDefinition, 'stat' | 'eitherStat'>, attributes: Attributes): keyof Attributes | null {
  return attack.stat && attack.eitherStat && attributes[attack.eitherStat] > attributes[attack.stat] ? attack.eitherStat : attack.stat;
}
export interface AttackDefinition {
  id: string;
  name: string;
  mode: 'melee' | 'ranged';
  count: number;
  sides: number;
  stat: keyof Attributes | null;
  /** A weapon that adds either of two stats (Core 3.2 p. 35, Short Sword STR/DEX) uses the wielder's higher one; `stat` is then the first. */
  eitherStat?: keyof Attributes;
  bonus: number;
  damageType: DamageType;
  range: number; // Maximum base-edge gap in meters, converted with reachRangeToMeters.
  canCrit: boolean;
  /** Spell attacks are not subject to a class's weapon proficiency for critical hits. */
  isSpell?: boolean;
  /** Damage that ignores a target's Armor. */
  ignoreArmor?: boolean;
  /** Vicious (Core p. 34): a crit rolls one additional damage die. */
  vicious?: boolean;
  /** A weapon's category when proficiency is not stat-based. */
  weaponType?: string;
  /** A spell with a Reach rather than a Range: foes in melee with the caster do not hinder it, as they hinder ranged attacks. */
  reach?: boolean;
  /** Rebuke: double damage against undead and the cowardly (Frightened; behind Cover once Cover exists, issue #421). */
  doubleVsCowardly?: boolean;
  /** Stat-block "(2×)": one action makes this many separate attack rolls against the same target. Absent means one. */
  strikes?: number;
  /** Conditions inflicted on a hero when the attack deals damage ("On damage, Dazed"). Reapplying has no extra effect. */
  onDamage?: AttackCondition[];
  /** Conditions inflicted on a successful attack roll, even if damage is reduced to zero. */
  onHit?: AttackCondition[];
  onCritical?: AttackCondition[];
  disadvantageNextAttack?: boolean;
  /** The stat-block "also hits the next closest creature": the same roll strikes whoever stands nearest the target, friend or foe. */
  chains?: boolean;
  /** Zap: on a miss the attacker takes half the rolled damage, of the attack's type, instead of the target. */
  backfire?: boolean;
  /** Weapon property Light (Core Rules, Melee Weapons): two of them can be dual wielded by anyone. */
  light?: boolean;
  /** Weapon property 2-handed: it needs both hands, so nothing can be held in the off hand. */
  twoHanded?: boolean;
  /**
   * Weapon property Load (Core Rules, Ranged Weapons): extra actions spent loading before each shot. A hero holds at most two actions, so
   * the shot takes that many actions (never more than a hero has) and as many action periods of windup as the book's actions add up to.
   */
  load?: number;
  /** The strength a weapon wants ("Req. 2 STR"). Shown with the weapon, like armor's, and not enforced: the book gives no penalty. */
  strengthRequired?: number;
  /** Weapons bought as a stack ("Stack of 4"). Ammunition is not tracked, so it is shown and not counted. */
  stack?: number;
}
/**
 * `withered`: Withering Touch, the target counts as undead for one combat round. `smoldering` (Flame Dart) does nothing on its own until the fight
 * ends; `slowed` (Ice Lance) halves speed and `blinded` (Snowblind) is Blind, each for one round.
 */
export type AttackCondition = 'dazed' | 'prone' | 'taunted' | 'withered' | 'smoldering' | 'slowed' | 'blinded';
/**
 * Monster stat-block rules that change how dice resolve or how a monster moves. Content composes them; the combat system
 * reads only these data, never a monster id (GMG pp. 6–7, 31).
 */
export type MonsterTrait =
  /** "Your attacks miss on 8": a primary die showing this value misses (so it also removes critical hits on that die). */
  | { id: 'inept'; missOn: number }
  /** "Attacks miss you on a 2 or less": attacks against this monster miss on a primary die this low. */
  | { id: 'mortal-panic'; missAtOrBelow: number }
  /** "You are Invisible until you attack": unseen and untargetable until its first attack, which has advantage. */
  | { id: 'sneak' }
  /** "When damaged, teleport 4": a surviving hit moves the monster this many meters away from the party. */
  | { id: 'spark-step'; meters: number }
  /** "Your allies are NOT Inept": while this monster lives, its encounter group's Inept trait is switched off. */
  | { id: 'strict-training' }
  /** Forest Wolves: one DC WIL save at engagement; fear ends when one pack member dies. */
  | { id: 'fearsome-howl'; dc: number }
  /** Flee combat when outnumbered. */
  | { id: 'pack-hunters' }
  /** Goblin taunts ("Haha, Missed Me!"): surviving an attack deals psychic damage back. `name` is the stat block's own wording. */
  | { id: 'survival-retaliation'; damage: number; name: string }
  | { id: 'charge'; meters: number }
  | { id: 'meat-shield'; family: string }
  /** Dire Wolf Pack Leader: once a round, takes a hit meant for an allied monster of `family` within `reach` (Reach/Range steps) without paying an action. */
  | { id: 'protector-of-the-pack'; family: string; reach: number }
  | { id: 'summon-after-attack'; template: string };
export type DamageModifiers = Partial<Record<DamageType, number>>;
export interface CombatProfile {
  maxHp: number;
  attributes: Attributes;
  defense: number;
  /** Actions a Defend reaction costs. Absent means 1; heroes in armor they are untrained in pay 2 (Core Rules p. 32). */
  defendCost?: number;
  armor: 0 | 10 | 20;
  maxWounds: number;
  movementDistance: number;
  /** Ancestry or other authoritative rules grant advantage on the Initiative d20. */
  initiativeAdvantage?: boolean;
  /** Initiative bonus added to the d20 when a fight begins. Content decides it (the book suggests DEX); class features can add to it. Absent means 0. */
  initiative?: number;
  attacks: AttackDefinition[];
  /** Monster stat-block rules. Absent for heroes. */
  traits?: MonsterTrait[];
  /** Saves with advantage or disadvantage; absent for opponents. */
  saves?: { advantage: (keyof Attributes)[]; disadvantage: (keyof Attributes)[] };
  /** Weapons outside this list cannot crit. Absent means every weapon. */
  weaponProficiency?: 'all' | (keyof Attributes)[];
  weaponTypes?: string[];
  /** Flat damage taken off every hit of that type. Hidden from browsers until the foe is assessed. */
  resistances?: DamageModifiers;
  /** Flat damage added to every hit of that type. Hidden from browsers until the foe is assessed. */
  weaknesses?: DamageModifiers;
  /** An undead creature (Rebuke deals it double damage). */
  undead?: boolean;
}
export interface HealthState {
  hp: number;
  maxHp: number;
  temporaryHp: number;
  wounds: number;
  maxWounds: number;
  /** Unspent Hit Dice (Core Rules p. 9). Heroes only; opponents have none. */
  hitDice?: number;
  maxHitDice?: number;
  /** Orc Relentless: one charge per Safe Rest; HP is the server's hero level. Absent on other units. */
  relentlessAvailable?: boolean;
  relentlessHp?: number;
  /** Halfling Elusive: one failed save turned into a success per Safe Rest. Absent on other units. */
  elusiveAvailable?: boolean;
  dying: boolean;
  dead: boolean;
}
export interface CombatState {
  /** Server-owned Taunt lifetime, projected for a persistent visual cue. */
  taunt?: { sourceId: string; remaining: number };
  nextAttackDisadvantage?: boolean;
  /** A charger has earned its advantage (straight-line distance covered) and holds it until its next attack roll. */
  charge?: boolean;
  /** A charger is partway through (or has finished) a straight run that can earn its advantage. */
  charging?: boolean;
  /** Dazed: the lost action has not come back yet. Clears when the unit next recovers an action. */
  dazed?: boolean;
  /** Dazed monster already in reach of its target: cannot move for a round, but keeps its attacks. */
  rooted?: boolean;
  /** Withering Touch: counts as undead until the round it was inflicted ends. */
  withered?: boolean;
  /** Smoldering: a minor status with no effect of its own that some fire spells read. Ends with the fight. */
  smoldering?: boolean;
  /** Slowed: half speed until the round it was inflicted ends. */
  slowed?: boolean;
  /** Blind: its attacks have disadvantage and attacks against it advantage, until the round it was inflicted ends. */
  blinded?: boolean;
  /** Charged: it took lightning damage this fight. Overload spends it. Ends with the fight. */
  charged?: boolean;
  /** True Strike: the next attack has advantage, ignores every disadvantage and reaches one space further. Ends with the fight. */
  trueStrike?: boolean;
  /** Entice: the next attack that hits deals 1d12 more damage. Ends with the fight. */
  enticed?: boolean;
  actions: number;
  actionRecoveryRemaining: number;
  rush: number;
  rushDisadvantage: number;
  targetId: string | null;
  attackId: string | null;
  order: 'idle' | 'move' | 'attack' | 'automatic' | 'stopped';
  /** Anticipate Danger is held: incoming Primary Dice are lowered by one and one action pip stays locked. */
  anticipating: boolean;
  /** Create an Opening is on this unit: the next hero attack against it raises its Primary Die by one. */
  opening: boolean;
  /** Seconds until the hero can Assess again. Absent means ready. */
  assessCooldown?: number;
  /** Seconds until the hero can toggle Anticipate Danger again. Absent means ready. */
  anticipateCooldown?: number;
  /** The Hide stance is held: the hero sneaks at half pace, holds its own attacks, and hides whenever no foe in the fight can see it. */
  hiding?: boolean;
  /** Seconds until the hero can toggle Hide again. Absent means ready. */
  hideCooldown?: number;
  /** The Run stance is held: the hero moves at double pace and regains actions at half the rate. */
  running?: boolean;
  /** Seconds until the hero can toggle Run again. Absent means ready. */
  runCooldown?: number;
  /** Set while a hero's one-action activity is under way. */
  windup?: ActionWindup | null;
  /** A hero who walked less than half its movement over the last `STEADY_WINDOW_SECONDS` holds one more action crystal. Absent means not steady. */
  steady?: boolean;
}
/** Seconds a hero's one-action activity takes before it happens: attack, a tactical action, using an item, and each action of a cast. Standing up from Prone takes the same time without an action. Reactions are exempt. */
export const HERO_ACTION_SECONDS = 2;
/** A monster's attack takes this long to land: half its six-second turn (the other half was movement). It still regains its one action every six seconds. */
export const MONSTER_ACTION_SECONDS = 3;
/** Action pips a healthy hero can hold. */
export const HERO_ACTIONS = 2;
/** How far back a hero's walking is counted to decide whether it is steady: under half of what its Speed covers in this time grants the extra crystal. */
export const STEADY_WINDOW_SECONDS = 4;
/** Monsters move freely and use one stat-block action per six-second round (GMG pp. 6–7). */
export const MONSTER_ACTIONS = 1;
/** What a unit is doing during that time. Any action is already spent; the effect lands when `remaining` reaches zero. */
export type WindupKind = 'attack' | 'assess' | 'opening' | 'stand' | 'item';
/** Action crystals a hero has: two, one more while steady, one while Dying. */
export function actionSlots(hero: { health: { dying: boolean }; combat: Pick<CombatState, 'steady'> }): number {
  return hero.health.dying ? 1 : HERO_ACTIONS + (hero.combat.steady ? 1 : 0);
}
/** Action pips a hero can hold: its crystals, one fewer while Anticipate Danger is held. Movement costs no action. */
export function actionCap(hero: { health: { dying: boolean }; combat: Pick<CombatState, 'anticipating' | 'windup' | 'steady'> }): number {
  return actionSlots(hero) - (hero.combat.anticipating ? 1 : 0);
}
/** Which hand a dual wielder's attack is struck with: the off hand alone, or both blades together for the once-a-round dual wielding benefit. Absent is the main hand. Presentation only. */
export type StrikeHand = 'off' | 'both';
export interface ActionWindup { kind: WindupKind; remaining: number; duration: number; attackId: string | null; targetId: string | null; hand?: StrikeHand }
/** Why a roll went the way it did: Advantage, Anticipate Danger, Sneak Attack. `kind` is its tone in the log. */
export interface RollTag { kind: 'good' | 'bad' | 'neutral'; label: string; detail?: string }
/** A number added to or taken from a roll's total; `die` is the die size when the term is itself a rolled die. */
export interface RollTerm { label: string; value: number; die?: number }
/**
 * The dice behind a combat log line, as the server rolled them. `dice` are the ones kept, Primary Die first;
 * `total` is the sum before `reductions` (armor, defense, resistance) and `result` what is left.
 */
export interface RollLog {
  kind: 'attack' | 'save' | 'initiative' | 'heal';
  /** The weapon, spell or check: "Longsword", "WIL save". */
  name?: string;
  sides: number;
  dice: number[];
  dropped: number[];
  explosions: number[];
  /** The Primary Die as rolled, when something moved it (Anticipate Danger, an Opening). */
  primaryFrom?: number;
  terms: RollTerm[];
  reductions: RollTerm[];
  total: number;
  result: number;
  outcome: 'miss' | 'hit' | 'crit' | 'success' | 'fail' | 'total';
  /** A save's difficulty. */
  dc?: number;
  tags: RollTag[];
}
export interface CombatEvent { id: number; text: string; roll?: RollLog }
export interface CombatVisualEvent {
  id: number; kind: 'strike' | 'impact' | 'block' | 'miss' | 'interpose' | 'assess' | 'anticipate' | 'heal' | 'mark' | 'command' | 'initiative' | 'condition' | 'teleport' | 'arc' | 'ability'; actorId: string; targetId: string;
  from: { x: number; z: number }; to: { x: number; z: number }; damage: number; critical: boolean;
  /** What kind of blow an `impact` was, so the target's reaction can suit it. */
  damageType?: DamageType;
  /** Actual HP lost to this impact, excluding temporary HP. Authoritative feedback for pain voices. */
  hpDamage?: number;
  /** An `impact` that killed its target, so the presentation can make the death of it (a decapitation is a lethal critical slash). */
  lethal?: boolean;
  /** An `impact` from an arrow, javelin or bolt rather than a blow in reach. */
  ranged?: boolean;
  /** The attack that caused this strike, impact or miss, to distinguish a bow arrow from a thrown weapon or a spell, or a maul's blow from a blade's. */
  attackId?: string;
  /** An `impact` that carried a Sneak Attack: its wound is drawn as a gory one. */
  sneakAttack?: boolean;
  /** The radiant damage of Judgment Dice an `impact` carried, already within `damage`, shown as its own number. */
  judgment?: number;
  /** The bandit passive that caused a missed attack, for creature voice reactions. */
  passive?: 'inept' | 'panic';
  /** Overrides the floating caption, e.g. an initiative result. */
  label?: string;
  /** Class spell that produced a visual event, when its presentation has a dedicated effect; for an `ability` event, the monster trait or attack that triggered.
   * An `ability` event's caption belongs to its actor (the monster that owns the ability); its target is whoever the ability touched. */
  abilityId?: string;
}
export interface ProjectileState {
  id: number; actorId: string; targetId: string;
  /** The weapon that fired, so a thrown javelin and an arrow look different in flight. */
  attackId: string;
  from: { x: number; z: number }; to: { x: number; z: number }; duration: number; remaining: number;
  /** The roll already missed: presentation only, so a lightning discharge (which strikes at once) can go astray instead of into the target. */
  misses?: true;
}
